/**
 * **视图增量。**
 *
 * 房主手上有一份完整 `GameState`，但发给每个玩家的是 `project()` 之后的视图。
 * 每次状态变化，房主要为**每个玩家各算一份**新视图，然后发**那个人的**增量
 * （`docs/netcode-s3.md` §6）。这就是本文件存在的理由。
 *
 * ⚠️ **不能照抄 `old_repo` 的 `#patchState`**：它每收到一条 patch 都要再无条件
 * 拉一次全量，于是 patch 机制零收益、还带一个并发竞态
 * （`docs/archive/netcode-s3-evaluation.md` §2.3）。
 *
 * ## 为什么用 immer 的 patch 格式
 *
 * 因为 `core/` 和视图本来就都是不可变数据，immer 已经在依赖里；
 * 而"从两份不可变数据里求差"如果没有现成的差分器，就得自己写一棵递归树 ——
 * 那正是最容易写出"漏了一个分支"的代码。
 *
 * ⚠️ immer 的一个坑：`produceWithPatches(base, () => next)`（配方**返回**新对象）
 * 产出的是**根节点的整份替换**，不是增量。要拿到细粒度增量，必须让配方
 * **就地修改草稿**。下面的 `syncInto` 就是这个"把草稿改成 next 的样子"的改写器。
 */

import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { z } from 'zod';

// immer 的 patch 能力是 opt-in 的，必须开一次
enablePatches();

export type { Patch };

/**
 * 线路上的 patch schema。
 *
 * 这一层必须校验：patch 是从网络来的，形状错了会让 `applyPatches` 抛出来。
 * 我们宁可回一张 `BadPayload` 报障单，也不要让异常从渲染路径里冒出来。
 */
export const PatchSchema = z.object({
  op: z.union([z.literal('replace'), z.literal('remove'), z.literal('add')]),
  path: z.array(z.union([z.string(), z.number()])),
  value: z.unknown().optional(),
});

export type PatchList = readonly Patch[];

export const PatchListSchema = z.array(PatchSchema);

/**
 * 一个**编译期**断言：`PatchSchema` 必须和 immer 的 `Patch` 等价。
 *
 * 这是从 `old_repo` 学来的一招（它也有一个 `__AssertPatchEqual`）：
 * zod schema 和它描述的类型之间没有自动联系，immer 升级改了 `Patch` 的形状
 * 我们不会收到任何提示。把它钉住，升级时 tsc 会直接报错。
 */
type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export type AssertPatchSchemaMatchesImmer<T extends Equals<Patch, z.infer<typeof PatchSchema>> = true> = T;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** 把 `parent[key]` 改成 `next` 的样子 —— 能就地改就就地改，改不了才整体替换 */
function syncValue(parent: Record<string, unknown> | unknown[], key: string | number, next: unknown): void {
  const current: unknown = (parent as Record<string | number, unknown>)[key];
  if (Object.is(current, next)) return;
  if (Array.isArray(current) && Array.isArray(next)) {
    syncArray(current, next);
    return;
  }
  if (isPlainObject(current) && isPlainObject(next)) {
    syncObject(current, next);
    return;
  }
  (parent as Record<string | number, unknown>)[key] = next;
}

function syncArray(current: unknown[], next: readonly unknown[]): void {
  const shared = Math.min(current.length, next.length);
  for (let i = 0; i < shared; i++) syncValue(current, i, next[i]);
  if (current.length > next.length) current.length = next.length;
  else for (let i = current.length; i < next.length; i++) current.push(next[i]);
}

function syncObject(current: Record<string, unknown>, next: Record<string, unknown>): void {
  for (const key of Object.keys(next)) syncValue(current, key, next[key]);
  for (const key of Object.keys(current)) if (!(key in next)) delete current[key];
}

/**
 * 求差：`prev → next` 的增量。
 *
 * 返回值可以直接 `JSON.stringify` 后上线，也可以喂给 `applyViewPatches`。
 * **没有变化时返回空数组**（而不是一条没用的根替换）——
 * 于是"重复推送同一份视图"在网络上是零字节。
 */
export function diffView<T>(prev: T, next: T): Patch[] {
  const [, patches] = produceWithPatches({ root: prev }, (draft) => {
    // 草稿的类型是 `WritableDraft<{root:T}>`，而 `syncValue` 只认普通对象/数组 ——
    // 它本来就是"按运行时的形状走"的改写器，所以在这一处转一次
    syncValue(draft as unknown as Record<string, unknown>, 'root', next);
  });
  // 剥掉包在外面那层 `root`，让路径是相对于视图本身的
  return patches.map((patch) => ({ ...patch, path: patch.path.slice(1) }));
}

/**
 * 应用增量。
 *
 * ⚠️ 和 `old_repo` 不同的地方：**这里不校验哈希，也不回退全量**。
 * 哈希校验是调用方（`session.ts`）的事，因为它才知道"对不上该找谁要全量"。
 * 这个函数只做一件事，而且会因为坏 patch **抛异常** —— 校验是它的调用者的责任。
 */
export function applyViewPatches<T>(prev: T, patches: PatchList): T {
  return applyPatches(prev as object, patches as Patch[]) as T;
}
